import type { DailyGuidance } from "@/lib/karma/daily";
import type { KarmaCoachGoal, KarmaNumerology } from "@/lib/karma/types";

export type KarmaMissionKind =
  | "starter"
  | "main"
  | "closing"
  | "bonus"
  | "insight";

export type KarmaEvidenceKind = "none" | "text" | "checks" | "photo";

export type KarmaMissionEvidence = {
  kind: KarmaEvidenceKind;
  prompt?: string;
  options?: string[];
};

export type KarmaMission = {
  id: string;
  kind: KarmaMissionKind;
  title: string;
  description: string;
  seconds: number;
  core: boolean;
  evidence: KarmaMissionEvidence;
  miniAlternative?: {
    title: string;
    description: string;
    seconds: number;
  };
};

export type KarmaCoachReward = {
  lesson: string;
  shadowTrigger: string;
  tomorrowFocus: string;
};

export type KarmaCoachPlan = {
  themeTitle: string;
  themeOneLiner: string;
  missions: KarmaMission[];
  closingQuestion: string;
  reward: KarmaCoachReward;
};

export const COACH_CORE_MISSION_IDS = [
  "core-starter",
  "core-main",
  "core-closing",
] as const;

export function getLocalDateKey(date: Date = new Date()) {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, "0");
  const d = String(date.getDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
}

function goalLabel(goal: KarmaCoachGoal) {
  switch (goal) {
    case "iliski":
      return "İlişki";
    case "para":
      return "Para";
    case "saglik":
      return "Sağlık";
    case "uretim":
      return "Üretim";
    case "denge":
    default:
      return "Denge";
  }
}

function computePinProfile(pin: number[]) {
  const counts: Record<number, number> = { 1: 0, 2: 0, 3: 0, 4: 0, 5: 0, 6: 0, 7: 0, 8: 0, 9: 0 };
  for (const raw of pin) {
    const n = Math.trunc(Number(raw));
    if (n >= 1 && n <= 9) counts[n] += 1;
  }

  const missing = Object.entries(counts)
    .filter(([, c]) => c === 0)
    .map(([d]) => Number(d));

  const dominant = Object.entries(counts)
    .filter(([, c]) => c >= 2)
    .map(([d]) => Number(d));

  return { counts, missing, dominant };
}

function microFromDigit(d: number) {
  switch (d) {
    case 1:
      return {
        title: "Net karar (1)",
        description: "20 saniye: Bugün tek bir karar yaz ve altına 'seçtim' de.",
        seconds: 20,
      };
    case 2:
      return {
        title: "Yumuşak bağ (2)",
        description:
          "20 saniye: Bir kişiye tek cümlelik teşekkür / hal hatır mesajı gönder.",
        seconds: 20,
      };
    case 3:
      return {
        title: "İfade kıvılcımı (3)",
        description: "20 saniye: Bugün söylemek istediğin tek cümleyi yaz.",
        seconds: 20,
      };
    case 4:
      return {
        title: "Düzen dokunuşu (4)",
        description: "20 saniye: Masanda/telefonunda tek bir şeyi yerine koy ya da sil.",
        seconds: 20,
      };
    case 5:
      return {
        title: "Hareket (5)",
        description: "20 saniye: Ayağa kalk, omuzları çevir, 5 derin nefes al.",
        seconds: 20,
      };
    case 6:
      return {
        title: "Şefkat (6)",
        description: "20 saniye: Bir bardak su iç ve 'bedenime iyi bakıyorum' de.",
        seconds: 20,
      };
    case 7:
      return {
        title: "Sessizlik (7)",
        description: "20 saniye: Gözleri kapat ve sadece nefesi say: 7 nefes.",
        seconds: 20,
      };
    case 8:
      return {
        title: "Bolluk adımı (8)",
        description:
          "20 saniye: Cüzdan/banka notuna bak ve tek bir para hedefi yaz.",
        seconds: 20,
      };
    case 9:
    default:
      return {
        title: "Bırakış (9)",
        description:
          "20 saniye: Bugün bırakacağın tek şeyi yaz ve altına 'serbest' de.",
        seconds: 20,
      };
  }
}

function closingQuestionForDaily(dailyNumber: number) {
  switch (dailyNumber) {
    case 1:
      return "Bugün hangi kararı verdin?";
    case 2:
      return "Bugün kimle/neyinle uyum kurdun?";
    case 3:
      return "Bugün neyi ifade ettin?";
    case 4:
      return "Bugün neyi kurdun / neyi düzenledin?";
    case 5:
      return "Bugün hangi yeniliğe cesaret ettin?";
    case 6:
      return "Bugün kendine nasıl şefkat gösterdin?";
    case 7:
      return "Bugün hangi iç sesi duydun?";
    case 8:
      return "Bugün hangi somut adımı attın?";
    case 9:
    default:
      return "Bugün neyi bıraktın / neyi tamamladın?";
  }
}

function rewardFor(args: {
  dailyNumber: number;
  goal: KarmaCoachGoal;
  destinyNumber: number;
  heart: number;
  expression: number;
}) {
  const { dailyNumber, goal, destinyNumber, heart, expression } = args;
  const g = goalLabel(goal);

  const baseByDay: Record<number, { lesson: string; shadow: string; tomorrow: string }> = {
    1: {
      lesson: "Başlatma enerjisini aktive ettin.",
      shadow: "Acelecilik ve kontrol ihtiyacı tetiklenebilir.",
      tomorrow: "Tek bir hedefi sabırla büyüt.",
    },
    2: {
      lesson: "Uyum ve sezgiyi aktive ettin.",
      shadow: "Kırılganlıkla birlikte alınganlık gelebilir.",
      tomorrow: "İletişimi yumuşat, net sınır koy.",
    },
    3: {
      lesson: "İfade ve neşeyi aktive ettin.",
      shadow: "Dağılma ve erteleme tetiklenebilir.",
      tomorrow: "Bir fikri seç, küçükçe somutlaştır.",
    },
    4: {
      lesson: "Sağlam zemin ve düzeni aktive ettin.",
      shadow: "Katılık ve mükemmeliyetçilik tetiklenebilir.",
      tomorrow: "Esnek kalırken sistemi sürdür.",
    },
    5: {
      lesson: "Değişim ve özgürlüğü aktive ettin.",
      shadow: "Sabırsızlık ve kaçış tetiklenebilir.",
      tomorrow: "Hareketi tek bir rotaya bağla.",
    },
    6: {
      lesson: "Şefkat ve sorumluluğu aktive ettin.",
      shadow: "Fazla yük alma tetiklenebilir.",
      tomorrow: "Kendini de listenin içine yaz.",
    },
    7: {
      lesson: "İçgörü ve derinliği aktive ettin.",
      shadow: "Aşırı düşünme ve kapanma tetiklenebilir.",
      tomorrow: "Sezgiye bir somut adım ekle.",
    },
    8: {
      lesson: "Güç ve sonuç alma enerjisini aktive ettin.",
      shadow: "Hırs ve sertlik tetiklenebilir.",
      tomorrow: "Gücü hizmetle dengele.",
    },
    9: {
      lesson: "Bırakış ve tamamlamayı aktive ettin.",
      shadow: "Duygusal yüklenme ve nostalji tetiklenebilir.",
      tomorrow: "Yeniye alan açacak tek bir seçim yap.",
    },
  };

  const byDay = baseByDay[dailyNumber] ?? baseByDay[1];

  return {
    lesson: `${byDay.lesson} (${g} odağı • Kader ${destinyNumber})`,
    shadowTrigger: `${byDay.shadow} (Kalp ${heart} • İfade ${expression})`,
    tomorrowFocus: `${byDay.tomorrow} (Yarın için tek odak: ${g})`,
  };
}

function mainActionText(args: {
  daily: DailyGuidance;
  goal: KarmaCoachGoal;
  destinyNumber: number;
  pinMissing: number[];
}) {
  const { daily, goal, destinyNumber, pinMissing } = args;
  const g = goalLabel(goal);
  const missingHint = pinMissing.length ? `PIN boşluk: ${pinMissing.join(",")}` : "PIN dengeli";

  const base = (() => {
    switch (daily.number) {
      case 4:
        return "Bir sistemi kur: 10 dakikada düzenle, sadeleştir, netleştir.";
      case 9:
        return "Bir şeyi kapat: yarım kalanı bitir ya da vedalaş.";
      case 3:
        return "Bir şeyi görünür kıl: yaz, söyle, paylaş.";
      case 8:
        return "Somut bir adım at: para/iş hedefini tek hamleyle ilerlet.";
      case 2:
        return "İlişkiyi yumuşat: dinle, anla, tek bir cümleyle bağ kur.";
      case 1:
        return "Net bir karar ver: seç ve başla.";
      case 5:
        return "Yeni bir hareket: rutinde küçük bir değişim.";
      case 6:
        return "Bakım ve şefkat: kendini destekleyen bir seçim.";
      case 7:
      default:
        return "Sessizlik + içgörü: 5 dakika düşün ve tek bir içgörü çıkar.";
    }
  })();

  const destinyTwist = (() => {
    switch (destinyNumber) {
      case 1:
        return "Bunu lider gibi sahiplen.";
      case 2:
        return "Bunu yumuşak iletişimle yap.";
      case 3:
        return "Bunu eğlenceli/yaratıcı bir şekilde yap.";
      case 4:
        return "Bunu planlayarak ve ölçerek yap.";
      case 5:
        return "Bunu esnek kalarak yap.";
      case 6:
        return "Bunu şefkatle ve sorumlulukla yap.";
      case 7:
        return "Bunu iç sesini dinleyerek yap.";
      case 8:
        return "Bunu somut sonuçla bitir.";
      case 9:
      default:
        return "Bunu bırakışla tamamla.";
    }
  })();

  const goalTwist = (() => {
    switch (goal) {
      case "para":
        return "Para odağı: tek bir finans adımı seç (bütçe, fiyat, hedef).";
      case "iliski":
        return "İlişki odağı: tek bir mesaj/konuşma/niyet seç.";
      case "saglik":
        return "Sağlık odağı: tek bir beden seçimi (su, yürüyüş, uyku).";
      case "uretim":
        return "Üretim odağı: tek bir çıktı üret (taslak, not, dosya).";
      case "denge":
      default:
        return "Denge odağı: tek bir alışkanlığı nazikçe düzenle.";
    }
  })();

  return `${base} ${destinyTwist} ${goalTwist} (${missingHint})`;
}

export function buildCoachPlan(args: {
  daily: DailyGuidance;
  numerology: KarmaNumerology;
  goal: KarmaCoachGoal;
}): KarmaCoachPlan {
  const { daily, numerology, goal } = args;
  const pinProfile = computePinProfile(numerology.pin);

  const themeTitle = `Enerji ${daily.number}: ${daily.title}`;
  const themeOneLiner = `${daily.focus} • Odak: ${goalLabel(goal)}`;

  const starter: KarmaMission = {
    id: "core-starter",
    kind: "starter",
    title: "Başlatıcı (60 sn)",
    description: daily.meditation,
    seconds: 60,
    core: true,
    evidence: { kind: "none" },
    miniAlternative: {
      title: "Mini başlatıcı (20 sn)",
      description: "20 saniye: 5 derin nefes al ve omuzları gevşet.",
      seconds: 20,
    },
  };

  const main: KarmaMission = {
    id: "core-main",
    kind: "main",
    title: "Ana eylem (2 dk)",
    description: mainActionText({
      daily,
      goal,
      destinyNumber: numerology.destinyNumber,
      pinMissing: pinProfile.missing,
    }),
    seconds: 120,
    core: true,
    evidence: {
      kind: "checks",
      prompt: "Kanıt",
      options: ["Yaptım", "Kısmen yaptım", "Erteledim"],
    },
    miniAlternative: {
      title: "Mini ana eylem (20 sn)",
      description: "20 saniye: Bu görevin en küçük adımını yaz ve şimdi tek bir hamle yap.",
      seconds: 20,
    },
  };

  const closingQuestion = closingQuestionForDaily(daily.number);

  const closing: KarmaMission = {
    id: "core-closing",
    kind: "closing",
    title: "Kapanış (30 sn)",
    description: `1 cümle yaz: ${closingQuestion}`,
    seconds: 30,
    core: true,
    evidence: { kind: "text", prompt: closingQuestion },
    miniAlternative: {
      title: "Mini kapanış (10 sn)",
      description: "10 saniye: Bugün için tek kelime seç ve yaz.",
      seconds: 10,
    },
  };

  const bonus1: KarmaMission = {
    id: "bonus-affirmation",
    kind: "bonus",
    title: "Bonus: Olumlama (20 sn)",
    description: daily.affirmation,
    seconds: 20,
    core: false,
    evidence: { kind: "none" },
    miniAlternative: {
      title: "Mini olumlama (10 sn)",
      description: "10 saniye: Olumlamayı tek nefeste içinden tekrarla.",
      seconds: 10,
    },
  };

  const pinDigit = pinProfile.missing[0] ?? (pinProfile.dominant[0] ?? daily.number);
  const micro = microFromDigit(pinDigit);
  const bonus2: KarmaMission = {
    id: "bonus-micro",
    kind: "bonus",
    title: `Bonus: ${micro.title}`,
    description: micro.description,
    seconds: micro.seconds,
    core: false,
    evidence: { kind: "none" },
    miniAlternative: {
      title: "Mini bonus (10 sn)",
      description: "10 saniye: Bu bonusun tek kelimesini seç ve tekrarla.",
      seconds: 10,
    },
  };

  const reward = rewardFor({
    dailyNumber: daily.number,
    goal,
    destinyNumber: numerology.destinyNumber,
    heart: numerology.heartDesireNumber,
    expression: numerology.expressionNumber,
  });

  return {
    themeTitle,
    themeOneLiner,
    missions: [starter, main, closing, bonus1, bonus2],
    closingQuestion,
    reward,
  };
}

// Backward-compatible export for older modules/HMR.
export type LegacyDailyMissionKind =
  | "meditation"
  | "affirmation"
  | "action"
  | "destiny"
  | "reflection";

export type LegacyDailyMission = {
  id: string;
  kind: LegacyDailyMissionKind;
  title: string;
  description: string;
  minutes?: number;
};

export function buildDailyMissions(args: {
  daily: DailyGuidance;
  numerology: KarmaNumerology;
}): LegacyDailyMission[] {
  const { daily, numerology } = args;

  const destiny = (() => {
    switch (numerology.destinyNumber) {
      case 1:
        return {
          title: "Liderlik pratiği",
          description:
            "Bugün tek bir hedef seç ve onu sahiplen: 1 karar al, 1 adım at, 1 şeyi netleştir.",
        };
      case 2:
        return {
          title: "İlişki şifası",
          description:
            "Bir kişiyi (kendin dahil) yumuşakça dinle: 5 dakika sadece dinle ve tek bir cümleyle anladığını geri yansıt.",
        };
      case 3:
        return {
          title: "İfade alanı",
          description:
            "Bugün kendini görünür kıl: 6 cümle yaz (günlük/not) ya da küçük bir şey paylaş (ses, metin, fotoğraf).",
        };
      case 4:
        return {
          title: "Düzen ritüeli",
          description:
            "Bir alan seç (çekmece/masa/telefon) ve 10 dakika sadeleştir. Bıraktığın her şey için bir teşekkür cümlesi kur.",
        };
      case 5:
        return {
          title: "Cesur yenilik",
          description:
            "Konfor alanını nazikçe genişlet: yeni bir rota, yeni bir kahve, yeni bir karar. Seçtiğin yeniliği not et.",
        };
      case 6:
        return {
          title: "Şefkat anlaşması",
          description:
            "Kendine bir bakım sözü ver: bugün 1 şeyden vazgeç (aşırı ekran, geç yatmak) ve 1 şey ekle (su, yürüyüş, nefes).",
        };
      case 7:
        return {
          title: "İç ses pratiği",
          description:
            "15 dakika sessizlik: bir sorunu yaz ve ardından 3 cevap olasılığı üret. 'Hangisi huzur veriyor?' diye sor.",
        };
      case 8:
        return {
          title: "Bolluk adımı",
          description:
            "Para/iş enerjisi: bugün tek bir somut adım at (fiyat güncelle, plan yap, bütçe çıkar, başvuru gönder).",
        };
      case 9:
      default:
        return {
          title: "Bırakma ve tamamlama",
          description:
            "Bitmemiş bir şeyi seç ve kapat: ya tamamla ya da 'bunu bırakıyorum' diyerek dosyayı kapat/çöpü boşalt/sil.",
        };
    }
  })();

  const reflection = (() => {
    const expression = numerology.expressionNumber;
    const heart = numerology.heartDesireNumber;

    const expText: Record<number, string> = {
      1: "Benim doğal gücüm: öncülük.",
      2: "Benim doğal gücüm: uyum ve arabuluculuk.",
      3: "Benim doğal gücüm: ifade ve neşe.",
      4: "Benim doğal gücüm: düzen ve istikrar.",
      5: "Benim doğal gücüm: özgürlük ve merak.",
      6: "Benim doğal gücüm: şefkat ve sorumluluk.",
      7: "Benim doğal gücüm: içgörü ve derinlik.",
      8: "Benim doğal gücüm: güç ve yönetim.",
      9: "Benim doğal gücüm: hizmet ve bilgelik.",
    };

    const heartText: Record<number, string> = {
      1: "Kalbim bugün netlik ve cesaret istiyor.",
      2: "Kalbim bugün bağ kurmak ve yumuşamak istiyor.",
      3: "Kalbim bugün oyun ve ifade istiyor.",
      4: "Kalbim bugün güven ve düzen istiyor.",
      5: "Kalbim bugün değişim ve hareket istiyor.",
      6: "Kalbim bugün şefkat ve aidiyet istiyor.",
      7: "Kalbim bugün yalnızlık ve düşünme istiyor.",
      8: "Kalbim bugün bolluk ve sonuç istiyor.",
      9: "Kalbim bugün bırakış ve tamamlanma istiyor.",
    };

    return {
      title: "Kısa farkındalık",
      description: `3 cümle yaz: ${expText[expression] ?? "Benim doğal gücüm."} ${heartText[heart] ?? "Kalbim bugün ne istiyor?"} Bugün bunu nasıl onurlandırırım?`,
    };
  })();

  return [
    {
      id: "daily-meditation",
      kind: "meditation",
      title: "Meditasyon",
      description: daily.meditation,
      minutes: 3,
    },
    {
      id: "daily-affirmation",
      kind: "affirmation",
      title: "Olumlama",
      description: daily.affirmation,
      minutes: 1,
    },
    {
      id: "daily-action",
      kind: "action",
      title: "Küçük adım",
      description: daily.action,
      minutes: 10,
    },
    {
      id: "destiny-focus",
      kind: "destiny",
      title: destiny.title,
      description: destiny.description,
      minutes: 10,
    },
    {
      id: "reflection",
      kind: "reflection",
      title: reflection.title,
      description: reflection.description,
      minutes: 5,
    },
  ];
}
